import React from "react";
import { Button } from "@/components/ui/button";
import { AlertCircle, ChevronRight, Loader2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatRelative } from "date-fns";
import {
  WorkflowRunStepOperation,
  WorkflowRunTask,
  AgentWorkflowRunTaskStatus,
  AgentWorkflowRunStepOperationStatus,
  AgentWorkflowRunStepOperationType,
} from "@/store/workflowRunStore";
import {
  RichActionType,
  getRichActionDisplayName,
  getRichActionSubtitle,
} from "./helpers/richActionHelpers";
import { useAuthStore, useHasPermission } from "@/store/authStore";
import { canUserApproveTask } from "@/lib/auth-utils";
import { Permission } from "@forgepoint/shared";

interface WorkflowRunViewRichViewOperationProps {
  id: string;
  operation?: WorkflowRunStepOperation;
  task?: WorkflowRunTask;
  richActionId?: RichActionType;
  onOpenRichActionPanel: (operationId: string) => void;
  richActionPanelOpen: boolean;
  onApprove: (taskId: string) => void;
  onDeny: (taskId: string) => void;
  isApproving: boolean;
  isDenying: boolean;
}

export const WorkflowRunViewRichViewOperation: React.FC<
  WorkflowRunViewRichViewOperationProps
> = ({
  id,
  operation,
  task,
  richActionId,
  onOpenRichActionPanel,
  richActionPanelOpen,
  onApprove,
  onDeny,
  isApproving,
  isDenying,
}) => {
  const user = useAuthStore((state) => state.user);
  const canApproveAllTasks = useHasPermission(Permission.TASKS_MANAGE);
  const canApprove = canUserApproveTask(user, task, canApproveAllTasks);

  if (!operation || !richActionId) {
    return null;
  }

  const updatedAt = new Date(operation.updatedAt);

  const subtitle =
    operation.operationParameters.type ===
      AgentWorkflowRunStepOperationType.TOOL_CALL &&
    typeof operation.operationParameters.input === "object" &&
    operation.operationParameters.input !== null
      ? getRichActionSubtitle(
          richActionId,
          operation.operationParameters.input as Record<string, unknown>,
        )
      : "Interactive preview";

  if (operation.status === AgentWorkflowRunStepOperationStatus.FAILED) {
    return (
      <div className="pl-11">
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between border rounded-lg p-3 cursor-default border-red-500/50 bg-red-50/5 opacity-60">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-red-500 shrink-0" />
              <div>
                <div className="font-medium text-sm">
                  {getRichActionDisplayName(richActionId)}
                </div>
                <div className="text-xs text-muted-foreground">
                  Failed to execute
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 justify-end">
              <span className="text-xs text-muted-foreground">
                Failed {formatRelative(updatedAt, new Date())}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isRequiresReview =
    task?.status === AgentWorkflowRunTaskStatus.PENDING_REVIEW;
  const isCancelled = task?.status === AgentWorkflowRunTaskStatus.CANCELLED;
  const isCompleted = task?.status === AgentWorkflowRunTaskStatus.APPROVED;
  const showStatusLabel = isRequiresReview || isCancelled || isCompleted;
  const statusLabelPrefix = isCancelled
    ? "Cancelled "
    : isCompleted
      ? "Completed "
      : isRequiresReview
        ? "Pending since "
        : "";

  return (
    <div className="pl-11">
      <div className="flex flex-col gap-2">
        <div
          className={cn(
            "group flex items-center justify-between",
            "border rounded-lg p-3 cursor-pointer transition-colors hover:bg-muted/50",
            isRequiresReview && "border-yellow-500/50 bg-yellow-50/5",
            isCancelled && "opacity-50",
            isCompleted && "border-green-500/50 bg-green-50/5",
          )}
          onClick={() => onOpenRichActionPanel(id)}
        >
          <div className="flex items-center gap-2">
            <div>
              <div className="font-medium text-sm">
                {getRichActionDisplayName(richActionId)}
              </div>
              <div className="text-xs text-muted-foreground">{subtitle}</div>
            </div>
          </div>
          <div className="flex items-center gap-2 justify-end">
            {showStatusLabel && (
              <span className="text-xs text-muted-foreground group-hover:opacity-100 transition-opacity opacity-0">
                {statusLabelPrefix}
                {formatRelative(updatedAt, new Date())}
              </span>
            )}

            {richActionPanelOpen ? (
              <X className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </div>
        </div>
        {isRequiresReview && task && canApprove && (
          <div className="flex gap-2">
            <Button
              className="bg-green-600 hover:bg-green-700 text-white"
              onClick={() => onApprove(task.id)}
              disabled={isApproving || isDenying}
            >
              {isApproving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Approve"
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => onDeny(task.id)}
              disabled={isApproving || isDenying}
            >
              {isDenying ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Cancel"
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
